import type { FieldValues, UseFormReturn } from "react-hook-form";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@apac/components/ui";
import {
  TextField,
  type TextFieldProps,
  type SelectFieldProps,
} from "@apac/form-ui";
import { SelectField } from "@apac/form-ui";
import { useCountryOptions, DriverLicenseFormFields } from "@apac/travelers";
import { IconNameEnum, useModuleBEM, useStyles } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { NotificationBanner } from "@tenant/ui";
import { APAC_TRAVELERS_NAME, ApacTravelersComponent } from "../../context";
import defaultStyles from "./styles.module.scss";

export type DriverLicenseFieldOverrides = {
  address?: Partial<TextFieldProps<FieldValues>>;
  issuingCity?: Partial<TextFieldProps<FieldValues>>;
  issuingCountry?: Partial<SelectFieldProps>;
  postalCode?: Partial<TextFieldProps<FieldValues>>;
};

export interface DriverLicenseProps {
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  fieldOverrides: DriverLicenseFieldOverrides;
  showAddress?: boolean;
  showCity?: boolean;
  missingInfoError?: boolean;
}

export const DriverLicense: React.FC<DriverLicenseProps> = ({
  formContext,
  fieldOverrides: {
    address = {},
    issuingCity = {},
    issuingCountry = {},
    postalCode = {},
  },
  showAddress = false,
  showCity = false,
  missingInfoError,
}) => {
  const COMPONENT_KEY = ApacTravelersComponent.DriverLicense;
  const styles = useStyles(APAC_TRAVELERS_NAME, COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();

  const countryOptions = useCountryOptions();

  return (
    <Accordion defaultValue="driver-license-details">
      <AccordionItem value="driver-license-details">
        {missingInfoError && (
          <NotificationBanner
            className={cn("message-banner")}
            iconName={IconNameEnum.warningCircle}
            title={t("apac.form.requiredInfoMessage")}
          />
        )}
        <AccordionTrigger className={cn("trigger")}>
          <div className={cn("title")}>
            {t("travelerForm.driverLicense.title")}
          </div>
        </AccordionTrigger>

        <AccordionContent>
          <div className={cn("container")}>
            {showAddress && (
              <TextField
                formContext={formContext}
                name={DriverLicenseFormFields.address}
                label={t("address")}
                placeholder={t("address")}
                {...address}
              />
            )}
            {/* TODO: add city selection component if we care, api requires city code in iso 3166-2,
            https://github.com/hopper-org/intl-localization-toolbox/blob/master/locations/src/main/resources/city.json */}
            {showCity && (
              <TextField
                formContext={formContext}
                name={DriverLicenseFormFields.issuingCity}
                label={t("travelerForm.driverLicense.issuingCity")}
                placeholder={t(
                  "travelerForm.driverLicense.issuingCityPlaceholder"
                )}
                {...issuingCity}
              />
            )}
            <SelectField
              formContext={formContext}
              name={DriverLicenseFormFields.issuingCountry}
              label={t("travelerForm.driverLicense.issuingCountry")}
              placeholder={t("select")}
              options={countryOptions}
              {...issuingCountry}
            />
            <TextField
              formContext={formContext}
              name={DriverLicenseFormFields.postalCode}
              label={t("travelerForm.driverLicense.zipCode")}
              placeholder={t("travelerForm.driverLicense.zipCode")}
              {...postalCode}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
